import { formatUsd } from '@console/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { CopyButton, ErrorText, Field } from '../../components/ui/misc'
import { useToast } from '../../components/ui/toast'
import { api, useActiveOrg, useBilling } from '../../lib/api'
import { authClient } from '../../lib/auth'
import { Section } from './SettingsLayout'

export function OrganizationPage() {
  const { org, canManage, role, can } = useActiveOrg()
  const qc = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()
  const [name, setName] = useState(org?.name ?? '')
  const [busy, setBusy] = useState(false)
  const [leaveOpen, setLeaveOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [err, setErr] = useState<unknown>(null)
  if (!org) return null

  const save = async () => {
    setBusy(true)
    const { error } = await authClient.organization.update({ organizationId: org.id, data: { name } })
    setBusy(false)
    if (error) return setErr(error)
    toast('Organization updated')
    void qc.invalidateQueries({ queryKey: ['me'] })
  }

  const leave = async () => {
    const { error } = await authClient.organization.leave({ organizationId: org.id })
    if (error) return setErr(error)
    qc.clear()
    navigate('/home')
  }

  return (
    <>
      <Section
        title="Organization"
        footer={
          canManage && (
            <Button onClick={save} loading={busy} disabled={!name.trim() || name === org.name}>
              Save
            </Button>
          )
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name">
            <input className="input" value={name} disabled={!canManage} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Organization ID" hint="Quote this when you contact support.">
            <div className="flex items-center gap-1">
              <input className="input font-mono !text-[12px]" value={org.id} readOnly />
              <CopyButton value={org.id} label="" />
            </div>
          </Field>
        </div>
        <div className="mt-3">
          <ErrorText error={err} />
        </div>
      </Section>
      {can('org.delete') && (
        <Section
          title="Delete organization"
          actions={
            <Button variant="secondary" className="text-danger" onClick={() => setDeleteOpen(true)}>
              Delete
            </Button>
          }
        />
      )}
      {deleteOpen && <DeleteOrganization org={org} onClose={() => setDeleteOpen(false)} />}
      {role !== 'owner' && (
        <Section
          title="Leave organization"
          actions={
            <Button variant="secondary" className="text-danger" onClick={() => setLeaveOpen(true)}>
              Leave
            </Button>
          }
        />
      )}
      <Modal
        open={leaveOpen}
        onClose={() => setLeaveOpen(false)}
        title={`Leave ${org.name}?`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setLeaveOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={leave}>
              Leave
            </Button>
          </>
        }
      >
        An admin will need to invite you again to rejoin.
      </Modal>
    </>
  )
}

/** Owners only: type the name to confirm. Keys stop working, everyone loses access, unused credit is not refunded. */
function DeleteOrganization({ org, onClose }: { org: { id: string; name: string }; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()
  const { data: billing } = useBilling()
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<unknown>(null)
  const balance = billing?.balanceNano ?? 0

  const remove = async () => {
    setBusy(true)
    setErr(null)
    try {
      await api('/api/org', { method: 'DELETE', body: { confirm } })
      qc.clear()
      toast(`${org.name} was deleted`)
      navigate('/home')
    } catch (e) {
      setErr(e)
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Delete ${org.name}?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={remove} loading={busy} disabled={confirm.trim() !== org.name}>
            Delete organization
          </Button>
        </>
      }
    >
      <ul className="list-disc space-y-1 pl-5 text-[13px] leading-relaxed text-ink-secondary">
        <li>Every API key stops working at once.</li>
        <li>All members lose access, and pending invitations are cancelled.</li>
        {balance > 0 && (
          <li>
            The remaining balance of <span className="font-medium text-ink">{formatUsd(balance)}</span> is not refunded automatically.
          </li>
        )}
      </ul>
      <div className="mt-4">
        <Field label={<>Type <span className="font-medium text-ink">{org.name}</span> to confirm</>}>
          <input className="input" value={confirm} autoFocus onChange={(e) => setConfirm(e.target.value)} />
        </Field>
      </div>
      <div className="mt-2">
        <ErrorText error={err} />
      </div>
    </Modal>
  )
}
